import { Box, Select, SimpleGrid, TextInput } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { IconMapPin } from '@tabler/icons-react'
import { ICreateClinicForm } from '@v2/domains/clinics/clinics.type'
import { UpdateLocationRequest } from '@v2/domains/location/location.types'
import { useState } from 'react'

import { GoogleMap } from 'src/common/components'
import { STATES } from 'src/common/config'

import InputGooglePlaces from '../InputGooglePlaces/InputGooglePlaces.component'
import classes from './address-form-part.module.scss'

export interface AddressFormData {
  streetAddress?: string
  city?: string
  state?: string
  zipCode?: string
}

export interface AddressFormPartProps {
  form: UseFormReturnType<any>
  disabled?: boolean
  required?: boolean
  showMap?: boolean
  mapId?: string
  addressPath?: string
}

const AddressFormPart = ({
  form,
  disabled = false,
  required = false,
  showMap = true,
  mapId = 'addressMap',
  addressPath,
}: AddressFormPartProps) => {
  const [location, setLocation] = useState<google.maps.LatLng | undefined>(
    undefined,
  )

  const ALL_STATES = Object.values(STATES).map(({ value, match }) => {
    return {
      label: value,
      value: match[0],
    }
  })

  const getAddressPath = () => {
    if (addressPath) return addressPath

    if (form.values?.location?.address) {
      return 'location.address'
    }

    if (form.values?.address) {
      return 'address'
    }

    return 'address'
  }

  const basePath = getAddressPath()

  const handleGooglePlaceSelect = (
    result: google.maps.places.PlaceResult | null,
  ) => {
    setLocation(result?.geometry?.location ?? undefined)

    if (result?.address_components) {
      const addressValues: AddressFormData = {}

      result.address_components.reverse().forEach((component) => {
        const types = component.types

        if (types.includes('street_number')) {
          addressValues.streetAddress = `${component.long_name} ${
            addressValues.streetAddress || ''
          }`
        }

        if (types.includes('route')) {
          addressValues.streetAddress = component.long_name
        }

        if (types.includes('sublocality') && !addressValues.city) {
          addressValues.city = component.long_name
        }

        if (types.includes('locality')) {
          addressValues.city = component.long_name
        }

        if (types.includes('administrative_area_level_1')) {
          addressValues.state = component.short_name
        }

        if (types.includes('postal_code')) {
          addressValues.zipCode = component.long_name
        }
      })

      Object.entries(addressValues).forEach(([key, value]) => {
        if (value) {
          form.setFieldValue(`${basePath}.${key}`, value)
        }
      })
    }
  }

  const getMapDefaultPlace = () => {
    const streetAddress =
      basePath === 'location.address'
        ? form.values?.location?.address?.streetAddress
        : form.values?.address?.streetAddress

    const city =
      basePath === 'location.address'
        ? form.values?.location?.address?.city
        : form.values?.address?.city

    return streetAddress ? `${city || ''} ${streetAddress}`.trim() : undefined
  }

  return (
    <Box>
      <SimpleGrid my="lg" cols={2} spacing="lg">
        <InputGooglePlaces
          label="Street Address"
          placeholder="Start typing your address..."
          value={
            (basePath === 'location.address'
              ? form.values?.location?.address?.streetAddress
              : form.values?.address?.streetAddress) || ''
          }
          onInputChange={(value) =>
            form.setFieldValue(`${basePath}.streetAddress`, value)
          }
          onChange={handleGooglePlaceSelect}
          required={required}
          error={form.errors?.address ?? ''}
          disabled={disabled}
        />

        <TextInput
          key={form.key(`${basePath}.city`)}
          label="City"
          placeholder="City"
          required={required}
          disabled={disabled}
          {...form.getInputProps(`${basePath}.city`)}
        />

        <Select
          key={form.key(`${basePath}.state`)}
          label="State"
          placeholder="Select state"
          data={ALL_STATES}
          searchable
          required={required}
          disabled={disabled}
          {...form.getInputProps(`${basePath}.state`)}
        />

        <TextInput
          key={form.key(`${basePath}.zipCode`)}
          label="Zip Code"
          placeholder="Zip Code"
          required={required}
          disabled={disabled}
          {...form.getInputProps(`${basePath}.zipCode`)}
        />
      </SimpleGrid>

      {showMap && (
        <div className={classes.mapSection}>
          <div className={classes.mapHeader}>
            <IconMapPin size={20} />
            <span>Location Preview</span>
          </div>
          <GoogleMap
            className={classes.map}
            id={mapId}
            location={location}
            defaultPlace={getMapDefaultPlace()}
            onLocationChange={setLocation}
          />
        </div>
      )}
    </Box>
  )
}

export default AddressFormPart
